import { fetchWithAuth } from "./api";
import { BASE_API_URL } from "./api";

export interface QuestionnaireOption {
  value: string;
  label: string;
  score?: number;
}

export type QuestionInputType =
  | "single_selection"
  | "multiple_selection"
  | "text"
  | "text_area"
  | "scale_1_10";

export interface QuestionnaireQuestion {
  id: string;
  text: string;
  order: number;
  options?: QuestionnaireOption[];
  score?: number;
  input_type?: QuestionInputType;
}

export interface QuestionnaireScoring {
  type: string;
  risk_threshold?: number;
  interpretation?: string;
}

export interface QuestionnaireSection {
  id: string;
  title: string;
  description?: string;
  response_type:
    | "multiple_selection"
    | "text_area"
    | "scale_1_10"
    | "single_choice"
    | "multiple_options"
    | "mixed"
    | "single_choice_mixed"
    | "text_list";
  questions: QuestionnaireQuestion[];
  options?: QuestionnaireOption[];
  min_items?: number;
  max_items?: number;
  scoring?: QuestionnaireScoring;
}

export interface QuestionnaireStructure {
  sections: QuestionnaireSection[];
}

export interface Questionnaire {
  id: number;
  name: string;
  code: string;
  description: string;
  structure: QuestionnaireStructure;
  scoring_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  completed?: boolean;
}

export interface Patient {
  id: number;
  name: string;
  email: string;
  email_verified_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface QuestionnaireAssignment {
  id: number;
  patient: Patient;
  expires_at: string | null;
  is_completed: boolean;
  access_token: string;
}

export interface QuestionnairesByTokenResponse {
  success: boolean;
  data: {
    assignment: QuestionnaireAssignment;
    questionnaires: Questionnaire[];
    completed: boolean;
  };
  message: string;
}

// Interface for the user's responses
export interface QuestionnaireResponses {
  [sectionId: string]: {
    [questionId: string]:
      | string
      | string[]
      | boolean
      | number
      | Array<{
          caracteristica: string;
          es_por_tdah: boolean;
          soy_asi: boolean;
        }>;
  };
}

// Interface for formatted question responses with both text and answer
export interface FormattedQuestionResponse {
  question: string;
  answer: string | string[] | boolean | number;
}

// Interface for formatted questionnaire responses
export interface FormattedQuestionnaireResponses {
  [sectionId: string]: {
    [questionId: string]: FormattedQuestionResponse;
  };
}

// Interface for the submit response payload
export interface SubmitQuestionnairePayload {
  questionnaire_id: number;
  responses: FormattedQuestionnaireResponses;
}

// Interface for the submit response
export interface SubmitQuestionnaireResponse {
  success: boolean;
  message: string;
}

// Interface for questionnaire results data
export interface QuestionnaireResultScore {
  raw_score: number;
  item_count?: number;
  average?: number;
  name: string;
  description: string;
  formula?: string;
}

export interface QuestionnaireResultInterpretation {
  scoring_type: string;
  calculated_at: string;
  scores: Record<string, QuestionnaireResultScore>;
  interpretation: string[];
  summary: string;
  interpretations: Record<string, string>;
  clinical_interpretations: string[];
  ai_enhanced_interpretation: string;
}

export interface AiLearningExample {
  ai_interpretation: string;
  prompt: string;
  id: number;
  quality_rating: number | null;
  corrected_interpretation: string | null;
  psychologist_feedback: string | null;
  reviewed: boolean;
}

export interface QuestionnaireResultResponse {
  success: boolean;
  data: {
    detail_id: number;
    questionnaire: {
      id: number;
      name: string;
      description: string;
      scoring_type: string;
    };
    patient: {
      id: number;
      name: string;
    };
    completed_at: string;
    responses: Record<string, Record<string, FormattedQuestionResponse>>;
    interpretation: QuestionnaireResultInterpretation;
    psychologist_feedback?: string;
    ai_learning_example?: AiLearningExample;
  };
  message: string;
}

// Interface for AI feedback
export interface AiFeedbackPayload {
  detail_id: number;
  interpretation_id: number;
  rating: number;
  corrected_interpretation?: string;
  feedback?: string;
  psychologist_conclusion?: string;
}

export interface AiFeedbackResponse {
  success: boolean;
  message: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export const questionnaireService = {
  /**
   * Get all questionnaires
   */
  async getQuestionnaires(): Promise<ApiResponse<Questionnaire[]>> {
    try {
      const response = await fetchWithAuth("/questionnaires");
      return response;
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
      return {
        success: false,
        message: "Failed to fetch questionnaires",
        data: [],
      };
    }
  },

  /**
   * Get a specific questionnaire by ID
   */
  async getQuestionnaire(id: number): Promise<ApiResponse<Questionnaire>> {
    try {
      const response = await fetchWithAuth(`/questionnaires/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching questionnaire with ID ${id}:`, error);
      return {
        success: false,
        message: "Failed to fetch questionnaire",
        data: {} as Questionnaire,
      };
    }
  },

  getQuestionnairesByToken: async (
    token: string
  ): Promise<QuestionnairesByTokenResponse> => {
    try {
      // Use fetch instead of fetchWithAuth since this is a public endpoint
      const response = await fetch(
        `${BASE_API_URL}/questionnaires/by-token/${token}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(
          `API request failed with status ${response.status}: ${errorText}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching questionnaires by token:", error);
      throw error;
    }
  },

  submitQuestionnaireResponses: async (
    token: string,
    payload: SubmitQuestionnairePayload
  ): Promise<SubmitQuestionnaireResponse> => {
    try {
      // Use the new URL structure with token in the path
      const response = await fetch(
        `${BASE_API_URL}/questionnaires/by-token/${token}/responses`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      console.log("Submit response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText);
        throw new Error(
          `API request failed with status ${response.status}: ${errorText}`
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error submitting questionnaire responses:", error);
      throw error;
    }
  },

  getQuestionnaireResults: async (
    detailId: number
  ): Promise<QuestionnaireResultResponse> => {
    try {
      console.log(`Fetching questionnaire results for detail ID: ${detailId}`);

      const data = await fetchWithAuth(`/questionnaire-results/${detailId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      console.log("Questionnaire results data received:", data);
      return data;
    } catch (error) {
      console.error("Error fetching questionnaire results:", error);
      throw error;
    }
  },

  // New function to submit AI feedback
  submitAiFeedback: async (
    payload: AiFeedbackPayload
  ): Promise<AiFeedbackResponse> => {
    try {
      console.log(`Submitting AI feedback for detail ID: ${payload.detail_id}`);

      const data = await fetchWithAuth(`/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log("AI feedback submission response:", data);
      return data;
    } catch (error) {
      console.error("Error submitting AI feedback:", error);
      throw error;
    }
  },
};
